/**
 * ChatMessage — Single chat bubble for the Prompt Assistant (Story 8.5).
 *
 * Renders a user message right-aligned and an assistant message left-aligned.
 * When the assistant turn carries `proposedChanges`, callers should render a
 * `ProposalCard` *below* this bubble so the analysis text and the diff stay
 * visually grouped without forcing a single component to do both.
 */

import { formatTime } from '@/lib/format-date';
import type { AssistantMessageRole } from '@/lib/promptAssistant';

export interface ChatMessageProps {
  role: AssistantMessageRole;
  content: string;
  /** Optional ISO timestamp shown muted under the bubble. */
  createdAt?: string;
  /** Optional badge showing token usage on assistant turns. */
  tokensTotal?: number | null;
  /** Optional latency badge on assistant turns. */
  latencyMs?: number | null;
}

function formatHm(iso: string | undefined): string | null {
  return formatTime(iso) || null;
}

export function ChatMessage({
  role,
  content,
  createdAt,
  tokensTotal,
  latencyMs,
}: ChatMessageProps) {
  const isUser = role === 'user';
  const isSystem = role === 'system';
  const align = isUser ? 'justify-end' : 'justify-start';
  const bubbleClass = isUser
    ? 'bg-primary text-primary-foreground rounded-2xl rounded-br-sm'
    : isSystem
      ? 'bg-muted text-muted-foreground italic rounded-2xl border'
      : 'bg-card text-card-foreground border rounded-2xl rounded-bl-sm';

  const time = formatHm(createdAt);
  const hasTokens = tokensTotal !== null && tokensTotal !== undefined;
  const hasLatency = latencyMs !== null && latencyMs !== undefined;
  const showFooter = !isUser && (time !== null || hasTokens || hasLatency);

  return (
    <div className={`flex w-full ${align} px-2`}>
      <div className="flex max-w-[80%] flex-col gap-1">
        {!isUser ? (
          <div className="text-xs font-medium text-muted-foreground">
            {isSystem ? 'Sistema' : 'Assistente'}
          </div>
        ) : null}
        <div className={`px-4 py-3 text-sm whitespace-pre-wrap ${bubbleClass}`}>{content}</div>
        {showFooter ? (
          <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
            {time ? <span>{time}</span> : null}
            {hasTokens ? <span>· {tokensTotal} tokens</span> : null}
            {hasLatency ? <span>· {latencyMs}ms</span> : null}
          </div>
        ) : null}
        {isUser && time ? (
          <div className="text-right text-[11px] text-muted-foreground">{time}</div>
        ) : null}
      </div>
    </div>
  );
}
